/**
 * <s-drag-marquee> — progressive enhancement for the CSS marquee track.
 *
 * The element is the track itself: it keeps its CSS animation (endless slide to the left, paused on hover).
 * This script only adds manual control, with no buttons:
 * - mouse or finger drag moves the cards left or right,
 * - horizontal trackpad swipe (or Shift + wheel) does the same,
 * and when the shopper lets go the animation carries on from the new position.
 * The track holds two identical copies, so positions wrap seamlessly at both ends.
 * If this script never runs, the CSS animation still works on its own.
 */
class SDragMarquee extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.pressing = false;
    this.dragged = false;
    this.frozen = false;
    this.x = 0;
    this.style.touchAction = 'pan-y';

    this.addEventListener('pointerdown', (event) => this.onDown(event), { signal });
    this.addEventListener('pointermove', (event) => this.onMove(event), { signal });
    this.addEventListener('pointerup', (event) => this.onUp(event), { signal });
    this.addEventListener('pointercancel', (event) => this.onUp(event), { signal });
    this.addEventListener('wheel', (event) => this.onWheel(event), { signal, passive: false });
    // A drag must not count as a click on the card underneath.
    this.addEventListener('click', (event) => {
      if (this.dragged) {
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
      }
    }, { signal, capture: true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.wheelTimer);
  }

  half() {
    return this.scrollWidth / 2;
  }

  normalize(x) {
    const half = this.half();
    if (half <= 0) return x;
    let value = x % half;
    if (value > 0) value -= half;
    return value;
  }

  currentX() {
    const transform = getComputedStyle(this).transform;
    if (!transform || transform === 'none') return 0;
    return new DOMMatrixReadOnly(transform).m41;
  }

  apply() {
    this.style.transform = `translateX(${this.x}px)`;
  }

  freeze() {
    if (this.frozen) return;
    const style = getComputedStyle(this);
    this.animated = style.animationName !== 'none';
    this.duration = parseFloat(style.animationDuration) || 0;
    this.x = this.currentX();
    this.style.animation = 'none';
    this.apply();
    this.frozen = true;
  }

  release() {
    if (!this.frozen) return;
    this.frozen = false;
    this.style.animation = '';
    if (!this.animated) return; // reduced motion: keep the position the shopper chose
    const half = this.half();
    const progress = half > 0 ? -this.x / half : 0;
    this.style.transform = '';
    this.style.animationDelay = `${-(progress * this.duration)}s`;
  }

  onDown(event) {
    if (event.button !== 0) return;
    if (event.target.closest('[contenteditable="true"]')) return; // let the editor place the caret
    this.pressing = true;
    this.dragged = false;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
  }

  onMove(event) {
    if (!this.pressing || event.pointerId !== this.pointerId) return;
    const dx = event.clientX - this.startX;
    if (!this.dragged) {
      if (Math.abs(dx) < 6) return;
      this.dragged = true;
      this.freeze();
      this.base = this.x;
      this.setPointerCapture(event.pointerId);
      this.dataset.dragging = '';
    }
    this.x = this.normalize(this.base + dx);
    this.apply();
  }

  onUp(event) {
    if (!this.pressing) return;
    this.pressing = false;
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    delete this.dataset.dragging;
    if (this.dragged) this.release();
  }

  onWheel(event) {
    let dx = 0;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) dx = event.deltaX;
    else if (event.shiftKey) dx = event.deltaY;
    if (!dx) return; // normal vertical scrolling keeps scrolling the page
    event.preventDefault();
    this.freeze();
    this.x = this.normalize(this.x - dx);
    this.apply();
    clearTimeout(this.wheelTimer);
    this.wheelTimer = setTimeout(() => this.release(), 400);
  }
}

if (!customElements.get('s-drag-marquee')) customElements.define('s-drag-marquee', SDragMarquee);
